import { useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { LifeBuoy, Send } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { sendSupportRequest } from "../services/supportService";

const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL as string | undefined;

/**
 * Contact-support page. Submits to the `sendSupportRequest` Cloud Function,
 * which emails the configured SUPPORT_EMAIL inbox via the same Trigger Email
 * pipeline as every other system email. The user's auth email is set as
 * Reply-To so support can hit Reply and reach them directly.
 *
 * Server-side instead of mailto: works the same for everyone regardless of
 * mail-client setup (no broken handoff for web Gmail / corporate machines /
 * Chromebooks). Respects SUPPRESS_EMAILS and EMAIL_OVERRIDE_RECIPIENT.
 */
export function SupportPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || submitting) return;
    setSubmitting(true);
    try {
      await sendSupportRequest({
        subject: subject.trim() || undefined,
        message: message.trim(),
        url: window.location.href,
      });
      showSuccess("Message sent. We'll be in touch shortly.");
      setSubject("");
      setMessage("");
    } catch (err) {
      const messageText =
        err instanceof Error
          ? err.message
          : "Something went wrong sending your message.";
      showError(messageText);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <PageHeader title="Contact Support" />
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Tell us what's going on and we'll get back to you. We'll automatically
        include your account info, the page you're on, and a timestamp so we
        can help faster.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LifeBuoy size={24} />
              <Typography variant="h6" component="h2">
                Send us a message
              </Typography>
            </Box>

            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Briefly, what's this about?"
              fullWidth
              disabled={submitting}
            />

            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe what happened or what you need help with."
              fullWidth
              multiline
              minRows={6}
              required
              disabled={submitting}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {SUPPORT_EMAIL ? (
                <Typography variant="body2" color="text.secondary">
                  Or email us directly at{" "}
                  <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover">
                    {SUPPORT_EMAIL}
                  </Link>
                </Typography>
              ) : (
                <span />
              )}
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  submitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Send size={18} />
                  )
                }
                disabled={!message.trim() || submitting}
              >
                {submitting ? "Sending…" : "Send message"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {!user && (
        <Alert severity="info" sx={{ mt: 3 }}>
          You'll need to be signed in to send a support message.
        </Alert>
      )}
    </Box>
  );
}
